import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Dimensions,
  Platform,
  Modal,
  Linking,
  Animated,
  ImageStyle,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from '../../components/common/Text';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import { palette } from '../../utils/palette';
import Button from '../../components/common/Button';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { getGooglePartnerFormId } from '../../config/googleForms';
import FeedbackForm from '../../components/shared/FeedbackForm';
import SupportForm from '../../components/shared/SupportForm';
import { useFeatureFlag, isFeatureEnabled } from '../../hooks/useFeatureFlags';
import { trackButtonClick, trackAnalyticsEvent } from '../../utils/analytics';
import { useBubbleAnimations } from '../../hooks/useBubbleAnimations';
import {
  PRODUCTS,
  TESTIMONIALS,
  BUBBLE_DATA,
  SPLAT_PARTICLES,
} from '../../data/landingData';
import FeatureCard from '../../components/landing/FeatureCard';
import TestimonialsSection from '../../components/landing/TestimonialsSection';
import StatsBarSection from '../../components/landing/StatsBarSection';
import HowItWorksSection from '../../components/landing/HowItWorksSection';
import SeasonShowcaseSection from '../../components/landing/SeasonShowcaseSection';
import PricingSection from '../../components/landing/PricingSection';

type LandingScreenNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;

// Landing page specific colors not in global palette
const HERO_SHAPE_RED = '#ff6b6b';
const HERO_SHAPE_TEAL = '#4ecdc4';
const HERO_SHAPE_BLUE = '#45b7d1';
const HERO_SHAPE_YELLOW = '#feca57';
const NEAR_BLACK = '#1a1a1a';
const MODAL_BORDER_DEEP = '#4f3d6f';
const MODAL_BG_DARK = '#483481';
const MODAL_BORDER_HOVER = '#5a3a9a';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// TESTIMONIALS, BUBBLE_DATA, SPLAT_PARTICLES and PRODUCTS imported from data/landingData.ts

const LandingScreen = () => {
  const navigation = useNavigation<LandingScreenNavigationProp>();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isMobile, screenWidth, isTablet } = useResponsiveLayout();
  /** Web 768–1024 still has `isMobile === false` but multi-column card grids overlap in RN Web; stack like phone. */
  const stackedLandingCards = isMobile || isTablet;
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const { data: pricingFlag } = useFeatureFlag('pricing_subscription');
  const isPricingEnabled = isFeatureEnabled(
    pricingFlag,
    'pricing_subscription'
  );

  // Set meta description for web
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      // Update meta description with testimonials keywords
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute(
        'content',
        'Discover your seasonal colour palette with ColourSense — AI-powered colour analysis that identifies the exact shades that flatter your skin tone, season, and undertone. Free to try, no sign-in needed. Plans from $0/month.'
      );

      // Update meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute(
        'content',
        'ColourSense, colour analysis, personal colour palette, skin tone analysis, colour season analysis, spring summer autumn winter colour type, what colours suit me, skin undertone test, AI colour analysis, real-time colour, colour matching, virtual drape, colour recommendations, fashion colours, style advice, free colour analysis, seasonal palette'
      );

      // Update Open Graph meta tags — upsert pattern
      const setOrCreateMeta = (
        selector: string,
        attr: string,
        value: string
      ) => {
        let el = document.querySelector(selector) as HTMLMetaElement | null;
        if (!el) {
          el = document.createElement('meta');
          const [attrKey, attrVal] = attr.split('=');
          el.setAttribute(attrKey, attrVal);
          document.head.appendChild(el);
        }
        el.setAttribute('content', value);
      };

      const OG_TITLE =
        'ColourSense — Stop Guessing. Discover Colours Made for You.';
      const OG_DESCRIPTION =
        'AI colour analysis identifies your seasonal palette and the exact shades that flatter you — in seconds. Free to try, no sign-in needed. Plans from $0.';
      const OG_IMAGE = 'https://mycoloursense.com/og-image.png';
      const CANONICAL_URL = 'https://mycoloursense.com';

      setOrCreateMeta(
        'meta[property="og:title"]',
        'property=og:title',
        OG_TITLE
      );
      setOrCreateMeta(
        'meta[property="og:description"]',
        'property=og:description',
        OG_DESCRIPTION
      );
      setOrCreateMeta(
        'meta[property="og:image"]',
        'property=og:image',
        OG_IMAGE
      );
      setOrCreateMeta(
        'meta[property="og:url"]',
        'property=og:url',
        CANONICAL_URL
      );
      setOrCreateMeta(
        'meta[property="og:type"]',
        'property=og:type',
        'website'
      );
      setOrCreateMeta(
        'meta[property="og:site_name"]',
        'property=og:site_name',
        'ColourSense'
      );

      // Twitter / X card tags
      setOrCreateMeta(
        'meta[name="twitter:card"]',
        'name=twitter:card',
        'summary_large_image'
      );
      setOrCreateMeta(
        'meta[name="twitter:title"]',
        'name=twitter:title',
        OG_TITLE
      );
      setOrCreateMeta(
        'meta[name="twitter:description"]',
        'name=twitter:description',
        OG_DESCRIPTION
      );
      setOrCreateMeta(
        'meta[name="twitter:image"]',
        'name=twitter:image',
        OG_IMAGE
      );
      setOrCreateMeta(
        'meta[name="twitter:site"]',
        'name=twitter:site',
        '@coloursense'
      );

      // Canonical link
      let canonical = document.querySelector(
        'link[rel="canonical"]'
      ) as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', CANONICAL_URL);

      // Add structured data for testimonials/reviews.
      // Google requires aggregateRating when multiple Review items are present.
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'ColourSense',
        description:
          'AI-powered colour analysis platform for discovering your seasonal colour palette, virtual drape testing, and personalised colour recommendations.',
        applicationCategory: 'LifestyleApplication',
        operatingSystem: 'Web, iOS, Android',
        url: 'https://mycoloursense.com',
        offers: [
          {
            '@type': 'Offer',
            name: 'Free',
            price: '0',
            priceCurrency: 'USD',
            description:
              '3 colour analyses per month — no credit card required',
          },
          {
            '@type': 'Offer',
            name: 'Plus',
            price: '4.99',
            priceCurrency: 'USD',
            description:
              'Unlimited colour analyses, full Virtual Drape, save & export palettes',
          },
          {
            '@type': 'Offer',
            name: 'Pro',
            price: '9.99',
            priceCurrency: 'USD',
            description:
              'Everything in Plus plus advanced AI, early feature access, and PDF reports',
          },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: 4.8,
          bestRating: 5,
          worstRating: 1,
          ratingCount: TESTIMONIALS.length,
        },
        review: TESTIMONIALS.map((testimonial) => ({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: testimonial.name,
          },
          reviewBody: testimonial.quote,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: testimonial.rating,
            bestRating: 5,
          },
        })),
      };

      // Remove existing structured data if any
      const existingScript = document.querySelector(
        'script[type="application/ld+json"]'
      );
      if (existingScript) {
        existingScript.remove();
      }

      // Add new structured data
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, []);
  const [showProductsMenu, setShowProductsMenu] = useState(false);
  const [selectedNavItem, setSelectedNavItem] = useState<string | null>(null);
  const [showBurgerMenu, setShowBurgerMenu] = useState(false);
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [productsDropdownAnimation] = useState(new Animated.Value(0));
  const [burgerMenuAnimation] = useState(new Animated.Value(0));
  const [testimonialsScrollAnimation] = useState(new Animated.Value(0));
  const [glowAnimation] = useState(new Animated.Value(0));
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toastAnim = useState(new Animated.Value(100))[0];

  const scrollViewRef = useRef<ScrollView>(null);
  const pricingRef = useRef<View>(null);

  const { floatAnims, popAnims, splatAnims, poppedBubbles, popBubble } =
    useBubbleAnimations();

  const handleGetStarted = () => {
    navigation.navigate('Register');
  };

  const handleSignIn = () => {
    navigation.navigate('Login');
  };

  const handleContinueAsGuest = () => {
    // Require authentication - redirect to sign in
    navigation.navigate('Login');
  };

  const handlePartnerWithUs = () => {
    const formId = getGooglePartnerFormId();
    if (!formId) {
      showToastMessage(
        'Partner form is not configured for this build. Set GOOGLE_PARTNER_FORM or EXPO_PUBLIC_GOOGLE_PARTNER_FORM in your deploy environment (e.g. Cloudflare Pages → Settings → Environment variables for Builds).'
      );
      return;
    }
    const googleFormUrl = `https://docs.google.com/forms/d/e/${formId}/viewform`;
    if (Platform.OS === 'web') {
      window.open(googleFormUrl, '_blank');
    } else {
      Linking.openURL(googleFormUrl);
    }
  };

  const handleFeedbackForm = () => {
    setShowFeedbackForm(true);
  };

  const handleSupportForm = () => {
    setShowSupportForm(true);
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    Animated.timing(toastAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      setTimeout(() => {
        Animated.timing(toastAnim, {
          toValue: 100,
          duration: 300,
          useNativeDriver: true,
        }).start(() => {
          setShowToast(false);
        });
      }, 2000);
    });
  };

  // Reset toast animation when component mounts
  useEffect(() => {
    toastAnim.setValue(100);
  }, []);

  const handleFAQ = () => {
    navigation.navigate('FAQ');
  };

  const handlePrivacyPolicy = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Open in new tab for web
      const currentUrl = window.location.origin;
      window.open(`${currentUrl}/privacy`, '_blank');
    } else {
      // Navigate normally for mobile
      navigation.navigate('PrivacyPolicy');
    }
  };

  const handleTermsOfService = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Open in new tab for web
      const currentUrl = window.location.origin;
      window.open(`${currentUrl}/terms`, '_blank');
    } else {
      // Navigate normally for mobile
      navigation.navigate('TermsOfService');
    }
  };

  const handleNavItemPress = (item: string) => {
    setSelectedNavItem(selectedNavItem === item ? null : item);
    if (item === 'products') {
      setShowProductsMenu(!showProductsMenu);
      setShowProductsDropdown(!showProductsDropdown);
    } else {
      setShowProductsMenu(false);
      setShowProductsDropdown(false);

      // Scroll to the appropriate section
      if (
        item === 'pricing' &&
        !isPricingEnabled &&
        pricingRef.current &&
        scrollViewRef.current
      ) {
        pricingRef.current.measureLayout(
          scrollViewRef.current.getInnerViewNode(),
          (x, y) => {
            scrollViewRef.current?.scrollTo({ y: y - 100, animated: true });
          },
          () => {}
        );
      }
    }
  };

  const navigateToFeature = useCallback(
    (id: string) => {
      const routes: Record<string, () => void> = {
        'color-analysis': () => navigation.navigate('PublicColorAnalysis'),
        'virtual-drape': () => navigation.navigate('PublicVirtualDrape'),
        'palette-generator': () => navigation.navigate('PublicColorGenerator'),
        'explore-colours': () => navigation.navigate('PublicExploreColours'),
      };
      (routes[id] ?? (() => navigation.navigate('Register')))();
      setShowProductsMenu(false);
    },
    [navigation]
  );

  useEffect(() => {
    const useNative = Platform.OS !== 'web';
    const startTestimonialsAnimation = () => {
      Animated.loop(
        Animated.sequence([
          Animated.timing(testimonialsScrollAnimation, {
            toValue: 1,
            duration: 35000,
            useNativeDriver: useNative,
          }),
          Animated.timing(testimonialsScrollAnimation, {
            toValue: 0,
            duration: 0,
            useNativeDriver: useNative,
          }),
        ])
      ).start();
    };
    startTestimonialsAnimation();
  }, [testimonialsScrollAnimation]);

  useEffect(() => {
    const startGlowAnimation = () => {
      Animated.loop(
        Animated.sequence([
          Animated.timing(glowAnimation, {
            toValue: 1,
            duration: 1500,
            useNativeDriver: false,
          }),
          Animated.timing(glowAnimation, {
            toValue: 0,
            duration: 1500,
            useNativeDriver: false,
          }),
        ])
      ).start();
    };
    startGlowAnimation();
  }, [glowAnimation]);

  const availableProducts = useMemo(
    () => PRODUCTS.filter((p) => p.available),
    []
  );
  const comingSoonProducts = useMemo(
    () => PRODUCTS.filter((p) => !p.available),
    []
  );

  return (
    <TouchableOpacity
      style={[styles.container, { backgroundColor: colors.card }]}
      activeOpacity={1}
      onPress={() => {
        if (showProductsMenu) {
          setShowProductsMenu(false);
        }
      }}
    >
      {/* Header */}
      <View
        style={[
          styles.header,
          isMobile && styles.headerMobile,
          {
            paddingTop: insets.top + 10,
            backgroundColor: colors.card,
            borderBottomColor: colors.border,
            ...(Platform.OS === 'web'
              ? ({
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                } as any)
              : {}),
          },
        ]}
      >
        <Image
          source={require('../../../assets/coloursense_logo_with_name.png')}
          style={
            [styles.logo, isMobile ? styles.logoMobile : null] as ImageStyle[]
          }
          resizeMode="contain"
        />

        {/* Navigation Menu */}
        {!isMobile && (
          <View style={styles.navMenu}>
            <TouchableOpacity
              style={[
                styles.navItem,
                selectedNavItem === 'products' && {
                  ...styles.navItemSelected,
                  backgroundColor: colors.authBackground,
                  borderColor: colors.primary,
                },
              ]}
              onPress={() => {
                trackButtonClick('Toolkit', 'landing-page-header', {
                  button_type: 'navigation',
                  action: 'toggle_dropdown',
                });
                handleNavItemPress('products');
              }}
            >
              <Text
                style={[
                  styles.navItemText,
                  {
                    color:
                      selectedNavItem === 'products'
                        ? colors.primary
                        : colors.text,
                  },
                ]}
              >
                Toolkit
              </Text>
              <Ionicons
                name="chevron-down"
                size={16}
                color={
                  selectedNavItem === 'products' ? colors.primary : colors.text
                }
              />
            </TouchableOpacity>

            {!isPricingEnabled && (
              <TouchableOpacity
                style={[
                  styles.navItem,
                  selectedNavItem === 'pricing' && {
                    ...styles.navItemSelected,
                    backgroundColor: colors.authBackground,
                    borderColor: colors.primary,
                  },
                ]}
                onPress={() => {
                  trackButtonClick('Pricing', 'landing-page-header', {
                    button_type: 'navigation',
                    action: 'scroll_to_section',
                  });
                  handleNavItemPress('pricing');
                }}
              >
                <Text
                  style={[
                    styles.navItemText,
                    {
                      color:
                        selectedNavItem === 'pricing'
                          ? colors.primary
                          : colors.text,
                    },
                  ]}
                >
                  Pricing
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Header Buttons */}
        {!isMobile && (
          <View style={styles.headerButtons}>
            <Button
              title="Partner with Us"
              onPress={handlePartnerWithUs}
              variant="primary"
              style={{
                ...styles.partnerButton,
                backgroundColor: colors.success,
              }}
              textStyle={{ ...styles.partnerButtonText, color: colors.card }}
              trackClick={true}
              trackingLocation="landing-page-header"
              trackingData={{
                button_name: 'Partner with Us',
                button_type: 'partnership',
                screen: 'landing',
              }}
            />

            <Button
              title="Sign In"
              onPress={handleSignIn}
              variant="outline"
              style={styles.signInButton}
              textStyle={styles.signInButtonText}
              hoverColor={colors.authBackground}
              trackClick={true}
              trackingLocation="landing-page-header"
              trackingData={{
                button_name: 'Sign In',
                button_type: 'authentication',
                screen: 'landing',
                action: 'sign_in',
              }}
            />

            <Button
              title="Sign Up Free →"
              onPress={handleGetStarted}
              variant="primary"
              style={styles.signUpButton}
              textStyle={styles.signUpButtonText}
              trackClick={true}
              trackingLocation="landing-page-header"
              trackingData={{
                button_name: 'Sign Up Free',
                button_type: 'authentication',
                screen: 'landing',
                action: 'sign_up',
              }}
            />
          </View>
        )}

        {/* Mobile Burger Menu Button */}
        {isMobile && (
          <TouchableOpacity
            style={[styles.burgerButton, styles.burgerButtonMobile]}
            onPress={() => {
              const isOpening = !showBurgerMenu;
              setShowBurgerMenu(isOpening);

              Animated.timing(burgerMenuAnimation, {
                toValue: isOpening ? 1 : 0,
                duration: 300,
                useNativeDriver: true,
              }).start();
            }}
          >
            <Ionicons
              name={showBurgerMenu ? 'close' : 'menu'}
              size={24}
              color={colors.text}
            />
          </TouchableOpacity>
        )}
      </View>

      {/* Mobile Burger Menu */}
      {isMobile && showBurgerMenu && (
        <Animated.View
          style={[
            styles.burgerMenu,
            {
              shadowColor: colors.cardText,
              transform: [
                {
                  translateX: burgerMenuAnimation.interpolate({
                    inputRange: [0, 1],
                    outputRange: [screenWidth, 0], // Slide from right edge to center
                  }),
                },
              ],
              backgroundColor: colors.card,
              borderTopColor: colors.borderLight,
            },
          ]}
        >
          <View style={styles.burgerMenuSection}>
            <TouchableOpacity
              style={styles.burgerMenuItem}
              onPress={() => {
                const isExpanding = !showProductsDropdown;
                setShowProductsDropdown(isExpanding);

                Animated.timing(productsDropdownAnimation, {
                  toValue: isExpanding ? 1 : 0,
                  duration: 200,
                  useNativeDriver: false,
                }).start();
              }}
            >
              <Ionicons
                name="color-palette-outline"
                size={20}
                color={colors.primary}
              />
              <Text style={[styles.burgerMenuItemText, { color: colors.text }]}>
                Toolkit
              </Text>
              <Ionicons
                name={showProductsDropdown ? 'chevron-up' : 'chevron-down'}
                size={16}
                color={colors.secondary}
                style={{ marginLeft: 'auto' }}
              />
            </TouchableOpacity>

            <Animated.View
              style={[
                styles.productsDropdown,
                {
                  maxHeight: productsDropdownAnimation.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 300], // Increased height for more items
                  }),
                  opacity: productsDropdownAnimation,
                  overflow: 'hidden',
                  borderColor: colors.borderLight,
                },
              ]}
            >
              {availableProducts.map((product) => (
                <TouchableOpacity
                  key={product.id}
                  style={styles.productsDropdownItem}
                  onPress={() => {
                    trackButtonClick(
                      product.title,
                      'landing-page-mobile-menu-products',
                      {
                        button_type: 'product_link',
                        product_id: product.id,
                        menu_type: 'burger_menu',
                      }
                    );
                    navigateToFeature(product.id);
                    setShowBurgerMenu(false);
                    setShowProductsDropdown(false);
                  }}
                >
                  <View
                    style={[
                      styles.productsDropdownIcon,
                      { backgroundColor: product.color },
                    ]}
                  >
                    <Ionicons
                      name={product.icon as any}
                      size={16}
                      color={colors.card}
                    />
                  </View>
                  <Text
                    style={[
                      styles.productsDropdownText,
                      { color: colors.text },
                    ]}
                  >
                    {product.title}
                  </Text>
                </TouchableOpacity>
              ))}

              {comingSoonProducts.map((product) => (
                <View
                  key={product.id}
                  style={[
                    styles.productsDropdownItem,
                    styles.comingSoonDropdownItem,
                    {
                      borderBottomColor: colors.border,
                      opacity: 0.7,
                      pointerEvents: 'none',
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.productsDropdownIcon,
                      { backgroundColor: product.color, opacity: 0.6 },
                    ]}
                  >
                    <Ionicons
                      name={product.icon as any}
                      size={16}
                      color={colors.card}
                    />
                  </View>
                  <Text
                    style={[
                      styles.productsDropdownText,
                      { color: colors.secondary },
                    ]}
                  >
                    {product.title}
                  </Text>
                </View>
              ))}
            </Animated.View>

            {!isPricingEnabled && (
              <TouchableOpacity
                style={styles.burgerMenuItem}
                onPress={() => {
                  trackButtonClick('Pricing', 'landing-page-mobile-menu', {
                    button_type: 'navigation',
                    action: 'scroll_to_section',
                    menu_type: 'burger_menu',
                  });
                  handleNavItemPress('pricing');
                  setShowBurgerMenu(false);
                }}
              >
                <Ionicons
                  name="card-outline"
                  size={20}
                  color={colors.primary}
                />
                <Text
                  style={[styles.burgerMenuItemText, { color: colors.text }]}
                >
                  Pricing
                </Text>
              </TouchableOpacity>
            )}
          </View>

          <View style={styles.burgerMenuSection}>
            <View
              style={[styles.divider, { backgroundColor: colors.borderLight }]}
            />

            <TouchableOpacity
              style={styles.burgerMenuItem}
              onPress={() => {
                trackButtonClick(
                  'Partner with Us',
                  'landing-page-mobile-menu',
                  {
                    button_type: 'partnership',
                    menu_type: 'burger_menu',
                  }
                );
                handlePartnerWithUs();
                setShowBurgerMenu(false);
              }}
            >
              <Ionicons
                name="people-outline"
                size={20}
                color={colors.primary}
              />
              <Text style={[styles.burgerMenuItemText, { color: colors.text }]}>
                Partner with Us
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.burgerMenuItem, styles.burgerSignUpItem]}
              onPress={() => {
                trackButtonClick('Sign Up Free', 'landing-page-mobile-menu', {
                  button_type: 'authentication',
                  action: 'sign_up',
                  menu_type: 'burger_menu',
                });
                handleGetStarted();
                setShowBurgerMenu(false);
              }}
            >
              <Ionicons name="person-add-outline" size={20} color="#FFFFFF" />
              <Text style={styles.burgerSignUpItemText}>Sign Up Free</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.burgerMenuItem}
              onPress={() => {
                trackButtonClick('Sign In', 'landing-page-mobile-menu', {
                  button_type: 'authentication',
                  action: 'sign_in',
                  menu_type: 'burger_menu',
                });
                handleSignIn();
                setShowBurgerMenu(false);
              }}
            >
              <Ionicons
                name="log-in-outline"
                size={20}
                color={colors.primary}
              />
              <Text style={[styles.burgerMenuItemText, { color: colors.text }]}>
                Sign In
              </Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      )}

      {/* Toolkit Dropdown Menu */}
      {showProductsMenu && (
        <TouchableOpacity
          style={[
            styles.toolkitDropdown,
            {
              top: insets.top + 68,
              backgroundColor: colors.card,
              borderColor: colors.border,
              ...(Platform.OS === 'web'
                ? ({
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                  } as any)
                : {
                    shadowColor: '#000',
                    shadowOpacity: 0.12,
                    shadowRadius: 16,
                    shadowOffset: { width: 0, height: 8 },
                    elevation: 8,
                  }),
            },
          ]}
          activeOpacity={1}
          onPress={(e) => {
            e.stopPropagation();
          }}
        >
          {/* Available tools — 2-column card grid */}
          <Text
            style={[styles.toolkitDropdownHeading, { color: colors.secondary }]}
          >
            Available Now
          </Text>
          <View style={styles.toolkitDropdownGrid}>
            {availableProducts.map((product) => (
              <TouchableOpacity
                key={product.id}
                style={[
                  styles.toolkitDropdownCard,
                  {
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    borderLeftColor: product.color,
                  },
                ]}
                onPress={() => {
                  trackButtonClick(
                    product.title,
                    'landing-page-toolkit-dropdown',
                    {
                      button_type: 'product_link',
                      product_id: product.id,
                      availability: 'available',
                    }
                  );
                  navigateToFeature(product.id);
                  setShowProductsMenu(false);
                }}
                activeOpacity={0.8}
              >
                <View style={styles.toolkitDropdownCardRow}>
                  <Ionicons
                    name={product.icon as any}
                    size={18}
                    color={product.color}
                    style={styles.toolkitDropdownCardIcon}
                  />
                  <View style={styles.toolkitDropdownCardTextCol}>
                    <Text
                      style={[
                        styles.toolkitDropdownCardTitle,
                        { color: colors.text },
                      ]}
                    >
                      {product.title}
                    </Text>
                    <Text
                      style={[
                        styles.toolkitDropdownCardSubtitle,
                        { color: colors.secondary },
                      ]}
                      numberOfLines={2}
                    >
                      {product.subtitle}
                    </Text>
                  </View>
                </View>
              </TouchableOpacity>
            ))}
          </View>

          {/* Coming soon — compact single-column list */}
          {comingSoonProducts.length > 0 && (
            <>
              <View
                style={[
                  styles.toolkitDropdownDivider,
                  { backgroundColor: colors.border },
                ]}
              />
              <Text
                style={[
                  styles.toolkitDropdownHeading,
                  { color: colors.secondary },
                ]}
              >
                Coming Soon
              </Text>
              {comingSoonProducts.map((product) => (
                <View
                  key={product.id}
                  style={[
                    styles.toolkitDropdownComingSoonItem,
                    { borderColor: colors.border, opacity: 0.65 },
                  ]}
                >
                  <Ionicons
                    name={product.icon as any}
                    size={16}
                    color={product.color}
                  />
                  <Text
                    style={[
                      styles.toolkitDropdownCardTitle,
                      { color: colors.secondary, flex: 1, marginLeft: 10 },
                    ]}
                  >
                    {product.title}
                  </Text>
                  <View
                    style={[
                      styles.comingSoonBadge,
                      { backgroundColor: colors.helpCenterText },
                    ]}
                  >
                    <Text
                      style={[styles.comingSoonText, { color: colors.card }]}
                    >
                      Soon
                    </Text>
                  </View>
                </View>
              ))}
            </>
          )}
        </TouchableOpacity>
      )}

      <ScrollView
        style={[styles.scrollView, { zIndex: 10 }]}
        showsVerticalScrollIndicator={false}
        ref={scrollViewRef}
        contentContainerStyle={
          isMobile
            ? { flexGrow: 0, width: '100%', maxWidth: '100%' }
            : Platform.OS === 'web'
              ? { flexGrow: 1 }
              : undefined
        }
        nestedScrollEnabled={true}
      >
        {/* Modern Hero Section */}
        <View
          style={[styles.heroSection, isMobile && styles.heroSectionMobile]}
        >
          {/* Floating Bubbles Background — tappable, poppable */}
          <View style={styles.bubblesContainer} pointerEvents="box-none">
            {BUBBLE_DATA.map((bubble, i) => {
              const floatAnim = floatAnims[i];
              const popAnim = popAnims[i];
              const splatAnim = splatAnims[i];
              const isPopped = poppedBubbles.has(i);
              const posStyle = {
                position: 'absolute' as const,
                width: bubble.size,
                height: bubble.size,
                borderRadius: bubble.size / 2,
                ...bubble.pos,
              };
              return (
                <React.Fragment key={i}>
                  {/* Burst ring — expands + fades when bubble is popped */}
                  <Animated.View
                    pointerEvents="none"
                    style={[
                      posStyle,
                      { backgroundColor: bubble.color },
                      {
                        transform: [
                          {
                            scale: popAnim.interpolate({
                              inputRange: [0, 1],
                              outputRange: [0.6, 2.8],
                            }),
                          },
                        ],
                        opacity: popAnim.interpolate({
                          inputRange: [0, 0.2, 0.6, 1],
                          outputRange: [0, 0.9, 0.2, 0],
                        }),
                      },
                    ]}
                  />
                  {/* Splat particles — 8 dots that fly outward on pop */}
                  {SPLAT_PARTICLES.map((p, pi) => {
                    const cx = bubble.size / 2 - p.size / 2;
                    const cy = bubble.size / 2 - p.size / 2;
                    const endX = cx + p.dist * Math.cos(p.rad);
                    const endY = cy + p.dist * Math.sin(p.rad);
                    return (
                      <Animated.View
                        key={`splat-${i}-${pi}`}
                        pointerEvents="none"
                        style={{
                          position: 'absolute',
                          ...(bubble.pos as any),
                          width: p.size,
                          height: p.size,
                          borderRadius: p.size / 2,
                          backgroundColor: bubble.color,
                          transform: [
                            {
                              translateX: splatAnim.interpolate({
                                inputRange: [0, 1],
                                outputRange: [cx, endX],
                              }),
                            },
                            {
                              translateY: splatAnim.interpolate({
                                inputRange: [0, 1],
                                outputRange: [cy, endY],
                              }),
                            },
                            {
                              scale: splatAnim.interpolate({
                                inputRange: [0, 0.15, 0.5, 1],
                                outputRange: [0, 1.4, 0.9, 0.3],
                              }),
                            },
                          ],
                          opacity: splatAnim.interpolate({
                            inputRange: [0, 0.08, 0.55, 1],
                            outputRange: [0, 1, 0.7, 0],
                          }),
                        }}
                      />
                    );
                  })}
                  {/* Floating bubble — hidden when popped, tappable otherwise */}
                  {!isPopped && (
                    <TouchableOpacity
                      onPress={() => popBubble(i)}
                      activeOpacity={0.85}
                      style={posStyle}
                    >
                      <Animated.View
                        style={{
                          flex: 1,
                          borderRadius: bubble.size / 2,
                          backgroundColor: bubble.color,
                          transform: [
                            {
                              translateY: floatAnim.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, bubble.ty],
                              }),
                            },
                            {
                              scale: floatAnim.interpolate({
                                inputRange: [0, 0.5, 1],
                                outputRange: bubble.s,
                              }),
                            },
                          ],
                          opacity: floatAnim.interpolate({
                            inputRange: [0, 0.5, 1],
                            outputRange: bubble.o,
                          }),
                        }}
                      />
                    </TouchableOpacity>
                  )}
                </React.Fragment>
              );
            })}
          </View>

          <View
            style={[styles.heroContent, isMobile && styles.heroContentMobile]}
          >
            {/* Badge */}
            <View
              style={[styles.heroBadge, isMobile && styles.heroBadgeMobile]}
            >
              <Text
                style={[
                  styles.heroBadgeText,
                  isMobile && styles.heroBadgeTextMobile,
                ]}
              >
                AI-Powered · Free to Try · No Sign-in Needed
              </Text>
            </View>

            {/* Hero Title */}
            <View style={styles.heroTitleContainer}>
              <Text
                style={[
                  styles.heroTitleProfessional,
                  isMobile && styles.heroTitleProfessionalMobile,
                  { color: colors.text },
                ]}
              >
                Stop Guessing.
              </Text>
              <Text
                style={[
                  styles.heroTitleProfessional,
                  isMobile && styles.heroTitleProfessionalMobile,
                  { color: colors.primary },
                ]}
              >
                Discover Colours Made for You.
              </Text>
            </View>

            {/* Season pills */}
            <View style={styles.seasonPills}>
              {[
                { name: 'Spring', color: '#FF9966' },
                { name: 'Summer', color: '#C9A8E0' },
                { name: 'Autumn', color: '#CC7722' },
                { name: 'Winter', color: '#4169E1' },
              ].map((s) => (
                <View
                  key={s.name}
                  style={[
                    styles.seasonPill,
                    {
                      backgroundColor: s.color + '22',
                      borderColor: s.color + '55',
                    },
                  ]}
                >
                  <View
                    style={[styles.seasonPillDot, { backgroundColor: s.color }]}
                  />
                  <Text style={[styles.seasonPillText, { color: s.color }]}>
                    {s.name}
                  </Text>
                </View>
              ))}
            </View>

            {/* Subtitle */}
            <Text
              style={[
                styles.heroSubtitle,
                isMobile && styles.heroSubtitleMobile,
                { color: colors.secondary },
              ]}
            >
              Real-time AI analysis identifies the exact colours that flatter
              your skin tone, season, and undertone — so every outfit lands.
            </Text>

            {/* CTA */}
            <View
              style={[styles.heroButtons, isMobile && styles.heroButtonsMobile]}
            >
              <View style={styles.buttonGlowContainer}>
                <Animated.View
                  style={[
                    styles.buttonGlow,
                    {
                      opacity: glowAnimation.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0.6, 1.0],
                      }),
                      shadowOpacity: glowAnimation.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0.4, 0.9],
                      }),
                    },
                  ]}
                />
                <TouchableOpacity
                  style={[
                    styles.gradientButton,
                    isMobile && styles.gradientButtonMobile,
                  ]}
                  onPress={() => {
                    trackButtonClick(
                      'Discover Your Perfect Colors',
                      'landing-page-hero',
                      {
                        button_type: 'primary_cta',
                        section: 'hero',
                      }
                    );
                    handleGetStarted();
                  }}
                  activeOpacity={0.8}
                >
                  <Text style={styles.gradientButtonText}>
                    Discover Your Perfect Colors
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
            {/* Mobile-only bubble discovery hint */}
            {isMobile && (
              <Text style={styles.bubbleHint}>✦ Tap a bubble to pop it</Text>
            )}
          </View>
        </View>

        {/* Stats Bar */}
        <StatsBarSection isMobile={stackedLandingCards} />

        {/* How It Works */}
        <View
          style={[
            styles.howItWorksBg,
            isMobile && styles.howItWorksBgMobile,
            stackedLandingCards && styles.landingSectionStackMobile,
          ]}
        >
          <HowItWorksSection colors={colors} isMobile={stackedLandingCards} />
        </View>

        {/* Season Showcase */}
        <SeasonShowcaseSection colors={colors} isMobile={stackedLandingCards} />

        {/* Featured Products Section */}
        <View style={[styles.section, isMobile && styles.sectionMobile]}>
          <View style={styles.sectionTitleContainer}>
            <Text
              style={[
                styles.sectionTitle,
                { color: colors.text },
                isMobile && styles.sectionTitleMobile,
              ]}
            >
              Your Colour Toolkit
            </Text>
          </View>
          <Text
            style={[
              styles.sectionSubtitle,
              { color: colors.secondary },
              isMobile && styles.sectionSubtitleMobile,
            ]}
          >
            Try every feature free — no sign-in required to get started.
          </Text>

          <View
            style={[
              styles.featuresGrid,
              stackedLandingCards && styles.featuresGridMobile,
            ]}
          >
            {availableProducts.map((product, index) => (
              <FeatureCard
                key={product.id}
                product={product}
                _index={index}
                isHovered={hoveredFeature === index}
                isMobile={stackedLandingCards}
                colors={colors}
                onPress={() => {
                  trackButtonClick(
                    product.title,
                    'landing-page-features-section',
                    {
                      button_type: 'feature_card',
                      feature_id: product.id,
                      click_location: 'card',
                    }
                  );
                  navigateToFeature(product.id);
                }}
                onTry={() => navigateToFeature(product.id)}
                onHoverIn={() => setHoveredFeature(index)}
                onHoverOut={() => setHoveredFeature(null)}
              />
            ))}
          </View>
        </View>

        {/* Social Proof Section */}
        <TestimonialsSection
          scrollAnimation={testimonialsScrollAnimation}
          colors={colors}
          isMobile={stackedLandingCards}
        />

        {/* Pricing Section */}
        {!isPricingEnabled && (
          <View ref={pricingRef}>
            <PricingSection
              colors={colors}
              isMobile={stackedLandingCards}
              onGetStarted={handleGetStarted}
            />
          </View>
        )}

        {/* Final CTA Section */}
        <LinearGradient
          colors={['#2D1B5E', '#4F3D6F', '#9475e1']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[
            styles.finalCtaSection,
            stackedLandingCards && styles.finalCtaSectionMobile,
            stackedLandingCards && styles.landingSectionStackMobile,
          ]}
        >
          {/* Decorative swatch dots */}
          <View style={styles.finalCtaSwatches} pointerEvents="none">
            {[
              '#FF9966',
              '#C9A8E0',
              '#CC7722',
              '#4169E1',
              '#10B981',
              '#EC4899',
            ].map((c, i) => (
              <View
                key={i}
                style={[
                  styles.finalCtaSwatch,
                  {
                    backgroundColor: c,
                    opacity: 0.25 + i * 0.05,
                    top: `${10 + i * 12}%` as any,
                    left: i % 2 === 0 ? (`${3 + i * 2}%` as any) : undefined,
                    right: i % 2 !== 0 ? (`${3 + i * 2}%` as any) : undefined,
                  },
                ]}
              />
            ))}
          </View>

          <View
            style={[
              styles.finalCtaContent,
              isMobile && styles.finalCtaContentMobile,
            ]}
          >
            {/* Eyebrow */}
            <View style={styles.finalCtaEyebrow}>
              <Text style={styles.finalCtaEyebrowText}>Start Free Today</Text>
            </View>

            <Text
              style={[
                styles.finalCtaTitle,
                isMobile && styles.finalCtaTitleMobile,
              ]}
            >
              Your Best Colours Are{'\n'}Waiting for You.
            </Text>

            <Text
              style={[
                styles.finalCtaSubtitle,
                isMobile && styles.finalCtaSubtitleMobile,
              ]}
            >
              Join 10,000+ people who discovered their seasonal palette. Free
              forever — upgrade only when you're ready.
            </Text>

            <Button
              title="Discover My Colours — It's Free"
              onPress={handleGetStarted}
              variant="primary"
              style={styles.finalCtaButton}
              textStyle={styles.finalCtaButtonText}
              trackClick={true}
              trackingLocation="landing-page-final-cta"
              trackingData={{
                button_name: 'Discover My Colours — Its Free',
                button_type: 'start_free_colour_analysis_landing_footer',
                screen: 'landing',
                offer: 'free_analysis',
              }}
            />
            <Text style={styles.finalCtaNote}>
              No credit card required · Instant results · Free plan available
            </Text>
          </View>
        </LinearGradient>

        {/* Footer */}
        <View
          style={[
            styles.footer,
            stackedLandingCards && styles.footerNarrow,
            { backgroundColor: colors.card, borderTopColor: colors.border },
          ]}
        >
          {/* Top row: brand + links */}
          <View
            style={[
              styles.footerTop,
              stackedLandingCards && styles.footerTopMobile,
            ]}
          >
            {/* Brand column */}
            <View
              style={[
                styles.footerBrand,
                stackedLandingCards && styles.footerBrandMobile,
              ]}
            >
              <Image
                source={require('../../../assets/trademark.png')}
                style={
                  [
                    styles.footerLogo,
                    stackedLandingCards && styles.footerLogoMobile,
                  ] as ImageStyle[]
                }
                resizeMode="contain"
              />
              <Text style={[styles.footerTagline, { color: colors.secondary }]}>
                Know your colours. Own your look.
              </Text>
              {/* Social icons */}
              <View style={styles.socialLinks}>
                {[
                  {
                    name: 'logo-facebook',
                    url: 'https://www.facebook.com/profile.php?id=61576212065319',
                    label: 'Facebook',
                  },
                  {
                    name: 'logo-instagram',
                    url: 'https://www.instagram.com/mycoloursense/',
                    label: 'Instagram',
                  },
                  {
                    name: 'logo-tiktok',
                    url: 'https://www.tiktok.com/@mycoloursense?lang=en',
                    label: 'TikTok',
                  },
                  {
                    name: 'logo-linkedin',
                    url: 'https://www.linkedin.com/company/coloursense/',
                    label: 'LinkedIn',
                  },
                ].map((social) => (
                  <TouchableOpacity
                    key={social.label}
                    style={[
                      styles.socialLink,
                      stackedLandingCards && styles.socialLinkMobile,
                      { borderColor: colors.border },
                    ]}
                    onPress={() => {
                      trackButtonClick(social.label, 'landing-page-footer', {
                        button_type: 'social_media',
                        platform: social.label.toLowerCase(),
                      });
                      Linking.openURL(social.url);
                    }}
                  >
                    <Ionicons
                      name={social.name as any}
                      size={18}
                      color={colors.secondary}
                    />
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Link groups */}
            <View
              style={[
                styles.footerLinkGroups,
                stackedLandingCards && styles.footerLinkGroupsMobile,
              ]}
            >
              {/* Product links */}
              <View style={styles.footerLinkGroup}>
                <Text style={[styles.footerGroupTitle, { color: colors.text }]}>
                  Product
                </Text>
                {[
                  {
                    label: 'Colour Analysis',
                    onPress: () => navigateToFeature('color-analysis'),
                  },
                  {
                    label: 'Virtual Drape',
                    onPress: () => navigateToFeature('virtual-drape'),
                  },
                  {
                    label: 'Palette Generator',
                    onPress: () => navigateToFeature('palette-generator'),
                  },
                  {
                    label: 'Colour Library',
                    onPress: () => navigateToFeature('explore-colours'),
                  },
                ].map((link) => (
                  <TouchableOpacity
                    key={link.label}
                    onPress={link.onPress}
                    style={styles.footerLinkItem}
                  >
                    <Text
                      style={[
                        styles.footerLinkText,
                        { color: colors.secondary },
                      ]}
                    >
                      {link.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Support links */}
              <View style={styles.footerLinkGroup}>
                <Text style={[styles.footerGroupTitle, { color: colors.text }]}>
                  Support
                </Text>
                {[
                  {
                    label: 'Feedback',
                    onPress: () => {
                      trackButtonClick('Feedback', 'landing-page-footer', {
                        button_type: 'footer_link',
                        link_type: 'feedback',
                      });
                      handleFeedbackForm();
                    },
                  },
                  {
                    label: 'Contact Us',
                    onPress: () => {
                      trackButtonClick('Support', 'landing-page-footer', {
                        button_type: 'footer_link',
                        link_type: 'support',
                      });
                      handleSupportForm();
                    },
                  },
                  {
                    label: 'FAQ',
                    onPress: () => {
                      trackButtonClick('FAQ', 'landing-page-footer', {
                        button_type: 'footer_link',
                        link_type: 'faq',
                      });
                      handleFAQ();
                    },
                  },
                ].map((link) => (
                  <TouchableOpacity
                    key={link.label}
                    onPress={link.onPress}
                    style={styles.footerLinkItem}
                  >
                    <Text
                      style={[
                        styles.footerLinkText,
                        { color: colors.secondary },
                      ]}
                    >
                      {link.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Legal links */}
              <View style={styles.footerLinkGroup}>
                <Text style={[styles.footerGroupTitle, { color: colors.text }]}>
                  Legal
                </Text>
                {[
                  {
                    label: 'Privacy Policy',
                    onPress: () => {
                      trackButtonClick('Privacy', 'landing-page-footer', {
                        button_type: 'footer_link',
                        link_type: 'privacy',
                      });
                      handlePrivacyPolicy();
                    },
                  },
                  {
                    label: 'Terms of Service',
                    onPress: () => {
                      trackButtonClick('Terms', 'landing-page-footer', {
                        button_type: 'footer_link',
                        link_type: 'terms',
                      });
                      handleTermsOfService();
                    },
                  },
                ].map((link) => (
                  <TouchableOpacity
                    key={link.label}
                    onPress={link.onPress}
                    style={styles.footerLinkItem}
                  >
                    <Text
                      style={[
                        styles.footerLinkText,
                        { color: colors.secondary },
                      ]}
                    >
                      {link.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          {/* Bottom bar */}
          <View
            style={[
              styles.footerBottom,
              stackedLandingCards && styles.footerBottomMobile,
              { borderTopColor: colors.border },
            ]}
          >
            <Text style={[styles.copyright, { color: colors.secondary }]}>
              © 2026 ColourSense. All rights reserved.
            </Text>
            <Text style={[styles.footerMadeWith, { color: colors.secondary }]}>
              Made for colour lovers worldwide
            </Text>
          </View>
        </View>
      </ScrollView>

      {/* Feedback Form Modal */}
      <FeedbackForm
        visible={showFeedbackForm}
        onClose={() => setShowFeedbackForm(false)}
        onSuccess={() => showToastMessage('Feedback sent successfully!')}
      />

      {/* Support Form Modal */}
      <SupportForm
        visible={showSupportForm}
        onClose={() => setShowSupportForm(false)}
        onSuccess={() =>
          showToastMessage('Support request submitted successfully!')
        }
      />

      {/* Toast Notification */}
      {showToast && (
        <Animated.View
          style={[
            styles.toastContainer,
            {
              transform: [{ translateY: toastAnim }],
            },
          ]}
        >
          <View
            style={[styles.toastContent, { backgroundColor: colors.primary }]}
          >
            <Ionicons name="checkmark-circle" size={20} color={palette.white} />
            <Text style={[styles.toastText, { color: palette.white }]}>
              {toastMessage}
            </Text>
          </View>
        </Animated.View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingBottom: 12,
    position: 'relative',
    zIndex: 10,
    borderBottomWidth: 1,
  },
  headerMobile: {
    paddingHorizontal: 16,
    paddingBottom: 10,
  },
  logo: {
    width: 180,
    height: 45,
  },
  logoMobile: {
    width: 130,
    height: 34,
  },
  navMenu: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    gap: 4,
  },
  navItemText: {
    fontSize: 15,
    fontWeight: '600',
    letterSpacing: 0.1,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  partnerButton: {
    paddingVertical: 8,
    paddingHorizontal: 18,
    borderRadius: 10,
    height: 40,
  },
  partnerButtonText: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.1,
  },
  signInButton: {
    paddingVertical: 8,
    paddingHorizontal: 18,
    borderRadius: 10,
    borderWidth: 1.5,
    height: 40,
  },
  signInButtonText: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.1,
  },
  signUpButton: {
    paddingVertical: 8,
    paddingHorizontal: 18,
    borderRadius: 10,
    height: 40,
    backgroundColor: '#9475e1',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 2px 8px rgba(148,117,225,0.35)' } as any)
      : {
          shadowColor: '#9475e1',
          shadowOpacity: 0.35,
          shadowRadius: 8,
          shadowOffset: { width: 0, height: 2 },
          elevation: 4,
        }),
  },
  signUpButtonText: {
    fontSize: 14,
    fontWeight: '800',
    letterSpacing: 0.1,
    color: '#FFFFFF',
  },
  burgerSignUpItem: {
    backgroundColor: '#9475e1',
    marginHorizontal: 0,
    borderRadius: 10,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 2px 8px rgba(148,117,225,0.35)' } as any)
      : {
          shadowColor: '#9475e1',
          shadowOpacity: 0.35,
          shadowRadius: 8,
          shadowOffset: { width: 0, height: 2 },
          elevation: 4,
        }),
  },
  burgerSignUpItemText: {
    fontSize: 16,
    fontWeight: '800',
    marginLeft: 12,
    color: '#FFFFFF',
  },
  toolkitDropdown: {
    position: 'absolute' as const,
    left: 0,
    right: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    borderWidth: 1,
    padding: 20,
    zIndex: 1000,
  },
  toolkitDropdownHeading: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  toolkitDropdownGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 4,
  },
  toolkitDropdownCard: {
    flex: 1,
    minWidth: 200,
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderLeftWidth: 4,
  },
  toolkitDropdownCardRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  toolkitDropdownCardIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  toolkitDropdownCardTextCol: {
    flex: 1,
    minWidth: 0,
  },
  toolkitDropdownCardTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 3,
  },
  toolkitDropdownCardSubtitle: {
    fontSize: 12,
    lineHeight: 16,
  },
  toolkitDropdownDivider: {
    height: 1,
    marginVertical: 14,
  },
  toolkitDropdownComingSoonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
  },
  dropdownSectionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  dropdownSection: {
    flex: 1,
    padding: 16,
  },
  dropdownSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 8,
    marginBottom: 4,
  },
  comingSoonItem: {
    opacity: 0.7,
  },
  dropdownIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  dropdownContent: {
    flex: 1,
    marginRight: 8,
  },
  dropdownTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
    flexShrink: 1,
  },
  dropdownSubtitle: {
    fontSize: 14,
    flexShrink: 1,
  },
  comingSoonBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 12,
  },
  comingSoonText: {
    fontSize: 12,
    fontWeight: '600',
  },
  scrollView: {
    flex: 1,
  },
  heroSection: {
    paddingHorizontal: 20,
    paddingVertical: 36,
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    backgroundColor: 'transparent',
    minHeight: 560,
  },
  heroSectionMobile: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    minHeight: 0,
    overflow: 'hidden',
    flexGrow: 0,
    alignSelf: 'stretch',
    width: '100%',
  },
  bubblesContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    marginBottom: 50,
  },
  bubble: {
    position: 'absolute',
    borderRadius: 1000,
  },
  bubble1: {
    width: 100,
    height: 100,
    top: '8%',
    left: '3%',
    backgroundColor: 'rgba(79, 61, 111, 0.6)',
  },
  bubble2: {
    width: 120,
    height: 120,
    top: '20%',
    right: '3%',
    backgroundColor: 'rgba(148, 117, 225, 0.6)',
  },
  bubble3: {
    width: 80,
    height: 80,
    top: '35%',
    left: '88%',
    backgroundColor: 'rgba(236, 72, 153, 0.5)',
  },
  bubble4: {
    width: 110,
    height: 110,
    top: '75%',
    left: '5%',
    backgroundColor: 'rgba(59, 130, 246, 0.5)',
  },
  bubble5: {
    width: 95,
    height: 95,
    top: '82%',
    right: '5%',
    backgroundColor: 'rgba(34, 197, 94, 0.5)',
  },
  bubble6: {
    width: 75,
    height: 75,
    top: '12%',
    left: '50%',
    backgroundColor: 'rgba(245, 158, 11, 0.5)',
  },
  bubble7: {
    width: 85,
    height: 85,
    top: '50%',
    left: '2%',
    backgroundColor: 'rgba(168, 230, 207, 0.4)',
  },
  bubble8: {
    width: 97,
    height: 97,
    top: '60%',
    right: '2%',
    backgroundColor: 'rgba(255, 182, 193, 0.5)',
  },
  bubble9: {
    width: 82,
    height: 82,
    top: '65%',
    right: '15%',
    backgroundColor: 'rgba(221, 160, 221, 0.3)',
  },
  bubble10: {
    width: 67,
    height: 67,
    top: '45%',
    left: '2%',
    backgroundColor: 'rgba(152, 251, 152, 0.4)',
  },
  bubble11: {
    width: 90,
    height: 90,
    top: '30%',
    left: '88%',
    backgroundColor: 'rgba(240, 230, 140, 0.4)',
  },
  bubble12: {
    width: 60,
    height: 60,
    top: '70%',
    left: '80%',
    backgroundColor: 'rgba(255, 160, 122, 0.3)',
  },
  bubble13: {
    width: 82,
    height: 82,
    top: '5%',
    left: '30%',
    backgroundColor: 'rgba(135, 206, 235, 0.5)',
  },
  bubble14: {
    width: 75,
    height: 75,
    top: '55%',
    left: '25%',
    backgroundColor: 'rgba(255, 20, 147, 0.4)',
  },
  bubble15: {
    width: 88,
    height: 88,
    top: '40%',
    right: '10%',
    backgroundColor: 'rgba(255, 165, 0, 0.4)',
  },
  bubble16: {
    width: 70,
    height: 70,
    top: '25%',
    left: '15%',
    backgroundColor: 'rgba(138, 43, 226, 0.4)',
  },
  bubble17: {
    width: 92,
    height: 92,
    top: '68%',
    left: '40%',
    backgroundColor: 'rgba(0, 191, 255, 0.4)',
  },
  bubble18: {
    width: 65,
    height: 65,
    top: '15%',
    right: '20%',
    backgroundColor: 'rgba(255, 105, 180, 0.4)',
  },
  bubble19: {
    width: 78,
    height: 78,
    top: '58%',
    right: '25%',
    backgroundColor: 'rgba(50, 205, 50, 0.4)',
  },
  bubble20: {
    width: 72,
    height: 72,
    top: '32%',
    left: '60%',
    backgroundColor: 'rgba(255, 140, 0, 0.4)',
  },
  seasonPills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
    marginBottom: 14,
    marginTop: 2,
  },
  seasonPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 100,
    borderWidth: 1,
    gap: 6,
  },
  seasonPillDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  seasonPillText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  heroBadge: {
    backgroundColor: 'rgba(148, 117, 225, 0.12)',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 100,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(148, 117, 225, 0.35)',
    ...(Platform.OS === 'web'
      ? {
          boxShadow: '0px 2px 12px rgba(148, 117, 225, 0.15)',
        }
      : {}),
  },
  heroBadgeText: {
    color: '#7C3AED',
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  heroBadgeMobile: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    marginBottom: 12,
    maxWidth: '100%',
  },
  heroBadgeTextMobile: {
    fontSize: 11,
    letterSpacing: 0.2,
  },
  heroTitleContainer: {
    alignItems: 'center',
    marginBottom: 6,
  },
  heroTitleProfessional: {
    fontSize: 54,
    fontWeight: '800',
    textAlign: 'center',
    lineHeight: 62,
    letterSpacing: -1.5,
    ...(Platform.OS === 'web'
      ? { textShadow: '0 1px 4px rgba(0, 0, 0, 0.05)' }
      : {
          textShadowColor: 'rgba(0, 0, 0, 0.05)',
          textShadowOffset: { width: 0, height: 1 },
          textShadowRadius: 4,
        }),
    marginBottom: 6,
  },
  heroTitleProfessionalMobile: {
    fontSize: 30,
    fontWeight: '800',
    textAlign: 'center',
    lineHeight: 36,
    letterSpacing: -0.8,
    ...(Platform.OS === 'web'
      ? { textShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }
      : {
          textShadowColor: 'rgba(0, 0, 0, 0.05)',
          textShadowOffset: { width: 0, height: 1 },
          textShadowRadius: 3,
        }),
    marginBottom: 4,
  },
  heroFeatures: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 24,
    marginBottom: 32,
    flexWrap: 'wrap',
  },
  featureItem: {
    alignItems: 'center',
    gap: 8,
  },
  featureIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  featureIconText: {
    fontSize: 20,
  },
  featureText: {
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
  },
  trustIndicators: {
    alignItems: 'center',
    marginTop: 24,
    gap: 8,
  },
  trustText: {
    fontSize: 14,
    fontWeight: '400',
  },
  trustStars: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  star: {
    fontSize: 16,
  },
  trustRating: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  buttonGlowContainer: {
    position: 'relative',
  },
  buttonGlow: {
    position: 'absolute',
    top: -6,
    left: -6,
    right: -6,
    bottom: -6,
    borderRadius: 36,
    backgroundColor: 'transparent',
    borderWidth: 3,
    borderColor: MODAL_BORDER_DEEP,
    shadowColor: MODAL_BORDER_DEEP,
    shadowOffset: { width: 0, height: 0 },
    shadowRadius: 30,
    elevation: 12,
    ...(Platform.OS === 'web'
      ? {
          boxShadow:
            '0px 0px 30px rgba(79, 61, 111, 0.8), 0px 0px 60px rgba(148, 117, 225, 0.6), 0px 0px 90px rgba(79, 61, 111, 0.4)',
        }
      : {}),
  },
  gradientButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 28,
    minHeight: 48,
    backgroundColor: palette.white,
    borderWidth: 1,
    borderColor: MODAL_BG_DARK,
    ...(Platform.OS === 'web'
      ? {
          backgroundImage:
            'linear-gradient(rgba(202, 183, 255, 0), rgba(202, 183, 255, 0.24)), linear-gradient(rgba(85, 37, 218, 0.32), rgba(85, 37, 218, 0.32))',
          backgroundBlendMode: 'normal, normal',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            backgroundImage:
              'linear-gradient(rgba(202, 183, 255, 0.1), rgba(202, 183, 255, 0.4)), linear-gradient(rgba(85, 37, 218, 0.5), rgba(85, 37, 218, 0.5))',
            borderColor: MODAL_BORDER_HOVER,
            transform: 'translateY(-1px)',
            boxShadow: '0px 4px 12px rgba(85, 37, 218, 0.3)',
          },
          '&:active': {
            transform: 'translateY(0px)',
            boxShadow: '0px 2px 8px rgba(85, 37, 218, 0.2)',
          },
        }
      : {}),
  },
  gradientButtonMobile: {
    alignSelf: 'stretch',
    maxWidth: 400,
    width: '100%',
    paddingHorizontal: 20,
  },
  gradientButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: NEAR_BLACK,
    textAlign: 'center',
  },
  heroContent: {
    maxWidth: 800,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    zIndex: 2,
    position: 'relative',
    paddingHorizontal: 4,
  },
  heroContentMobile: {
    minWidth: 0,
    alignSelf: 'stretch',
  },
  heroTitle: {
    fontSize: 48,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 56,
    letterSpacing: 0.5,
    ...(Platform.OS === 'web'
      ? { textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }
      : {
          textShadowColor: 'rgba(0, 0, 0, 0.3)',
          textShadowOffset: { width: 0, height: 2 },
          textShadowRadius: 4,
        }),
    fontStyle: 'normal',
    textTransform: 'none',
  },
  heroTitleMobile: {
    fontSize: 32,
    lineHeight: 38,
    marginBottom: 12,
    fontWeight: '600',
    letterSpacing: 0.3,
    ...(Platform.OS === 'web'
      ? { textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }
      : {
          textShadowColor: 'rgba(0, 0, 0, 0.3)',
          textShadowOffset: { width: 0, height: 2 },
          textShadowRadius: 4,
        }),
    fontStyle: 'normal',
    textTransform: 'none',
  },
  heroSubtitle: {
    fontSize: 19,
    textAlign: 'center',
    marginBottom: 22,
    lineHeight: 28,
    fontWeight: '400',
    letterSpacing: 0.1,
    maxWidth: 640,
  },
  heroSubtitleMobile: {
    fontSize: 15,
    marginBottom: 18,
    lineHeight: 25,
    fontWeight: '400',
    letterSpacing: 0.05,
    width: '100%',
  },
  heroButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
    marginTop: 8,
  },
  bubbleHint: {
    marginTop: 16,
    fontSize: 12,
    fontWeight: '500',
    color: 'rgba(148, 117, 225, 0.7)',
    letterSpacing: 0.3,
    textAlign: 'center',
  },
  heroButtonsMobile: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
  },
  primaryButton: {
    borderRadius: 28,
    overflow: 'hidden',
    height: 40,
  },
  primaryButtonMobile: {
    borderRadius: 28,
    height: 40,
  },
  primaryButtonGradient: {
    paddingVertical: 8,
    paddingHorizontal: 28,
    alignItems: 'center',
    height: 40,
  },
  primaryButtonGradientMobile: {
    paddingVertical: 6,
    paddingHorizontal: 20,
    height: 40,
  },
  primaryButtonText: {
    fontWeight: '600',
  },
  primaryButtonTextMobile: {},
  secondaryButton: {
    paddingVertical: 8,
    paddingHorizontal: 28,
    borderRadius: 28,
    borderWidth: 2,
    height: 40,
  },
  secondaryButtonMobile: {
    paddingVertical: 6,
    paddingHorizontal: 20,
    borderRadius: 28,
    borderWidth: 1,
    height: 40,
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButtonTextMobile: {
    fontSize: 14,
  },
  howItWorksBg: {
    backgroundColor: 'rgba(148, 117, 225, 0.05)',
  },
  howItWorksBgMobile: {},
  /** Stacked landing blocks on narrow viewports — avoids RN Web flex stretch overlap */
  landingSectionStackMobile: {
    flexGrow: 0,
    minWidth: 0,
    width: '100%',
    alignSelf: 'stretch',
  },
  section: {
    paddingHorizontal: 12,
    paddingVertical: 28,
  },
  sectionMobile: {
    paddingHorizontal: 16,
    paddingTop: 18,
    paddingBottom: 28,
    width: '100%',
    flexGrow: 0,
  },
  sectionTitleContainer: {
    width: '100%',
    alignItems: 'center',
    marginBottom: 6,
  },
  sectionTitle: {
    fontSize: 32,
    fontWeight: '800',
    textAlign: 'center',
    letterSpacing: -0.8,
    lineHeight: 40,
    paddingTop: 4,
    paddingBottom: 2,
    width: '100%',
  },
  sectionTitleMobile: {
    fontSize: screenWidth < 380 ? 22 : 24,
    lineHeight: screenWidth < 380 ? 30 : 32,
    paddingTop: 2,
    paddingBottom: 2,
  },
  sectionSubtitle: {
    fontSize: 17,
    textAlign: 'center',
    marginBottom: 20,
    fontWeight: '400',
    letterSpacing: 0.1,
  },
  sectionSubtitleMobile: {
    fontSize: 14,
    marginBottom: 14,
    lineHeight: 21,
    width: '100%',
  },
  featuresGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    gap: 14,
    paddingHorizontal: 12,
    width: '100%',
    minWidth: 0,
  },
  featuresGridMobile: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    alignItems: 'stretch',
    alignContent: 'flex-start',
    width: '100%',
    minWidth: 0,
    flexGrow: 0,
    paddingHorizontal: 0,
    gap: 16,
  },
  featureCard: {
    flex: 1,
    minWidth: 300,
    maxWidth: 520,
    padding: 24,
    borderRadius: 20,
    borderWidth: 1,
    alignItems: 'flex-start',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 5,
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: 260,
    overflow: 'hidden',
  },
  cardBody: {
    flex: 1,
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: 20,
  },
  cardBodyMobile: {
    marginBottom: 14,
  },
  cardTag: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 100,
    marginBottom: 14,
    alignSelf: 'flex-start',
  },
  cardTagText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  cardSubtitle: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 10,
    lineHeight: 22,
  },
  cardSubtitleMobile: {
    fontSize: 13,
    marginBottom: 8,
    lineHeight: 19,
  },
  featureCardPair: {
    ...(Platform.OS === 'web'
      ? {
          flex: 1,
          minWidth: 300,
          maxWidth: 520,
        }
      : {}),
  },
  featureCardMobile: {
    flex: 0,
    width: '100%',
    minWidth: 0,
    minHeight: 0,
    padding: 20,
  },
  featureCardHovered: {
    transform: [{ scale: 1.03 }],
    shadowOpacity: 0.2,
    shadowRadius: 24,
    elevation: 8,
    borderWidth: 2,
    borderColor: 'rgba(124, 58, 237, 0.3)',
  },
  cardAccentBar: {
    height: 5,
    marginHorizontal: -24,
    marginTop: -24,
    marginBottom: 20,
    alignSelf: 'stretch',
  },
  cardAccentBarMobile: {
    marginHorizontal: -20,
    marginTop: -20,
    marginBottom: 16,
  },
  featureIconLarge: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  featureIconHovered: {
    transform: [{ scale: 1.1 }],
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  featureTitle: {
    fontSize: 24,
    fontWeight: '800',
    marginBottom: 4,
    textAlign: 'left',
    letterSpacing: -0.5,
    lineHeight: 30,
  },
  featureTitleMobile: {
    fontSize: 20,
    lineHeight: 26,
    letterSpacing: -0.3,
  },
  featureTitleHovered: {
    fontSize: 24,
    fontWeight: '800',
    color: 'rgba(124, 58, 237, 0.9)',
  },
  featureDescription: {
    fontSize: 15,
    textAlign: 'left',
    marginBottom: 0,
    lineHeight: 23,
    opacity: 0.85,
  },
  featureDescriptionMobile: {
    fontSize: 14,
    lineHeight: 21,
  },
  tryButton: {
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 12,
    borderWidth: 0,
    height: 44,
    alignSelf: 'stretch',
  },
  tryButtonHovered: {
    opacity: 0.9,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  tryButtonText: {
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: 0.1,
  },
  tryButtonTextHovered: {
    fontSize: 15,
    fontWeight: '700',
  },
  socialProofSection: {
    paddingHorizontal: 20,
    paddingVertical: 32,
  },
  testimonialsHeader: {
    alignItems: 'center',
    marginBottom: 24,
  },
  testimonialsTitle: {
    fontSize: 40,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: -0.8,
  },
  testimonialsSubtitle: {
    fontSize: 18,
    textAlign: 'center',
    lineHeight: 28,
    maxWidth: 500,
  },
  testimonialsContainer: {
    flexDirection: 'row',
    overflow: 'hidden',
    marginBottom: 20,
  },
  testimonialsScrollView: {
    flexDirection: 'row',
    gap: 16,
  },
  testimonialCard: {
    width: 320,
    minWidth: 320,
    maxWidth: 320,
    height: 280,
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 6,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  testimonialHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  starsContainer: {
    flexDirection: 'row',
    gap: 2,
  },
  quoteIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  testimonialContent: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  testimonialQuote: {
    fontSize: 16,
    lineHeight: 24,
    fontStyle: 'italic',
    fontWeight: '400',
  },
  testimonialFooter: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.05)',
    paddingTop: 16,
  },
  testimonialAuthor: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  authorAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  authorInitial: {
    fontSize: 16,
    fontWeight: '600',
  },
  authorInfo: {
    flex: 1,
  },
  testimonialName: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  testimonialRole: {
    fontSize: 13,
    fontWeight: '400',
  },
  testimonialsStats: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  statItem: {
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  statNumber: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
  statDivider: {
    width: 1,
    height: 40,
    backgroundColor: 'rgba(0,0,0,0.1)',
    marginHorizontal: 10,
  },
  pricingGrid: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
    flexWrap: 'wrap',
    marginTop: 10,
  },
  pricingGridMobile: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    marginTop: 10,
  },
  pricingCard: {
    width: (screenWidth - 80) / 2,
    maxWidth: 280,
    minWidth: 240,
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  pricingCardMobile: {
    width: (screenWidth - 80) / 1,
    maxWidth: 380,
    minWidth: 280,
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  popularBadge: {
    position: 'absolute',
    top: -10,
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 12,
  },
  popularBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  pricingPlan: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  pricingPlanPremium: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  pricingPrice: {
    fontSize: 36,
    fontWeight: '800',
    marginBottom: 4,
  },
  pricingPricePremium: {
    fontSize: 36,
    fontWeight: '800',
    marginBottom: 4,
  },
  pricingPeriod: {
    fontSize: 14,
    marginBottom: 24,
  },
  pricingPeriodPremium: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 24,
  },
  pricingFeatures: {
    width: '100%',
    marginBottom: 24,
  },
  pricingFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  pricingFeatureText: {
    fontSize: 14,
    marginLeft: 8,
  },
  pricingFeatureTextPremium: {
    fontSize: 14,
    marginLeft: 8,
  },
  pricingButton: {
    width: '100%',
    paddingVertical: 8,
    borderRadius: 28,
    borderWidth: 2,
    alignItems: 'center',
    height: 40,
  },
  pricingButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  pricingButtonPremium: {
    width: '100%',
    paddingVertical: 8,
    borderRadius: 28,
    alignItems: 'center',
    height: 40,
  },
  pricingButtonTextPremium: {
    fontSize: 16,
    fontWeight: '600',
  },
  footer: {
    paddingHorizontal: 40,
    paddingTop: 48,
    paddingBottom: 0,
    borderTopWidth: 1,
    width: '100%',
    minWidth: 0,
  },
  footerNarrow: {
    paddingHorizontal: 16,
    paddingTop: 40,
  },
  footerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 40,
    gap: 40,
    width: '100%',
    minWidth: 0,
  },
  footerTopMobile: {
    flexDirection: 'column',
    gap: 32,
    flexGrow: 0,
  },
  footerBrand: {
    flexGrow: 0,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: 220,
    gap: 12,
  },
  footerBrandMobile: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    maxWidth: '100%',
    gap: 0,
  },
  footerLogo: {
    width: 140,
    height: 56,
  },
  footerLogoMobile: {
    width: 120,
    height: 48,
  },
  footerTagline: {
    fontSize: 13,
    lineHeight: 19,
    opacity: 0.7,
    fontWeight: '500',
  },
  socialLinks: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  socialLink: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  socialLinkMobile: {
    width: 44,
    height: 44,
    borderRadius: 12,
  },
  footerLinkGroups: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    flexDirection: 'row',
    gap: 40,
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
  },
  footerLinkGroupsMobile: {
    width: '100%',
    justifyContent: 'space-between',
    gap: 24,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    flexGrow: 0,
  },
  footerLinkGroup: {
    gap: 10,
    minWidth: 100,
    flexGrow: 0,
    flexShrink: 0,
  },
  footerGroupTitle: {
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  footerLinkItem: {
    paddingVertical: 2,
  },
  footerLinkText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
  },
  footerBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    paddingVertical: 20,
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
    minWidth: 0,
  },
  footerBottomMobile: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
  },
  copyright: {
    fontSize: 12,
    opacity: 0.6,
    fontWeight: '500',
  },
  footerMadeWith: {
    fontSize: 12,
    opacity: 0.5,
    fontWeight: '500',
    fontStyle: 'italic',
  },
  // Preview Modal Styles
  previewModal: {
    flex: 1,
  },
  previewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
  },
  closeButton: {
    padding: 8,
  },
  previewTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  placeholder: {
    width: 40,
  },
  previewContent: {
    flex: 1,
  },
  previewSection: {
    padding: 20,
  },
  previewImage: {
    width: '100%',
    height: 300,
    borderRadius: 12,
    marginBottom: 20,
  },
  previewDetails: {
    alignItems: 'center',
  },
  previewProductTitle: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  previewSubtitle: {
    fontSize: 16,
    marginBottom: 16,
    textAlign: 'center',
  },
  previewDescription: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 24,
    textAlign: 'center',
  },
  featuresList: {
    width: '100%',
    marginBottom: 32,
  },
  featuresTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
  },
  previewTryButton: {
    width: '100%',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  previewTryButtonText: {
    fontSize: 18,
    fontWeight: '600',
  },
  comingSoonButton: {
    width: '100%',
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
  },
  comingSoonButtonText: {
    fontSize: 18,
    fontWeight: '600',
  },
  navItemSelected: {
    borderRadius: 10,
    borderWidth: 1.5,
  },
  burgerButton: {
    padding: 8,
    borderRadius: 10,
  },
  burgerButtonMobile: {
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  burgerMenu: {
    position: 'absolute',
    top: 50,
    left: 0,
    right: 0,
    borderTopWidth: 1,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    zIndex: 999,
    paddingVertical: 20,
  },
  burgerMenuSection: {
    marginBottom: 24,
  },
  burgerMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginBottom: 4,
  },
  burgerMenuItemText: {
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 12,
  },
  productsDropdown: {
    marginLeft: 20,
    marginRight: 20,
    marginTop: 0,
    marginBottom: 0,
    borderRadius: 0,
    borderWidth: 0,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  productsDropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },
  productsDropdownIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  productsDropdownText: {
    fontSize: 14,
    fontWeight: '500',
  },
  divider: {
    height: 1,
    width: '80%',
    alignSelf: 'center',
    marginVertical: 8,
  },
  comingSoonDropdownItem: {
    opacity: 0.7,
  },
  // Final CTA Section Styles
  finalCtaSection: {
    paddingHorizontal: 32,
    paddingVertical: 64,
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
  },
  finalCtaSectionMobile: {
    paddingHorizontal: 20,
    paddingVertical: 44,
  },
  finalCtaSwatches: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  finalCtaSwatch: {
    position: 'absolute',
    width: 90,
    height: 90,
    borderRadius: 45,
  },
  finalCtaEyebrow: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 100,
    marginBottom: 16,
  },
  finalCtaEyebrowText: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  finalCtaContent: {
    maxWidth: 640,
    width: '100%',
    alignItems: 'center',
    zIndex: 1,
  },
  finalCtaContentMobile: {
    minWidth: 0,
    paddingHorizontal: 4,
  },
  finalCtaTitle: {
    fontSize: 44,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 50,
    letterSpacing: -1.2,
    color: '#FFFFFF',
  },
  finalCtaTitleMobile: {
    fontSize: 28,
    lineHeight: 36,
    letterSpacing: -0.5,
  },
  finalCtaSubtitle: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 26,
    lineHeight: 25,
    fontWeight: '400',
    color: 'rgba(255,255,255,0.8)',
    maxWidth: 480,
  },
  finalCtaSubtitleMobile: {
    fontSize: 14,
    lineHeight: 21,
    marginBottom: 20,
  },
  finalCtaButton: {
    paddingVertical: 14,
    paddingHorizontal: 40,
    borderRadius: 14,
    marginBottom: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.97)',
    borderWidth: 0,
    height: 54,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web'
      ? {
          boxShadow: `0px 8px 24px 0px rgba(0,0,0,0.2)`,
        }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.2,
          shadowRadius: 24,
          elevation: 10,
        }),
  },
  finalCtaButtonText: {
    fontSize: 16,
    fontWeight: '800',
    color: '#2D1B5E',
    letterSpacing: 0.1,
  },
  finalCtaNote: {
    fontSize: 13,
    textAlign: 'center',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.65)',
    letterSpacing: 0.2,
  },
  toastContainer: {
    position: 'absolute',
    bottom: Platform.OS === 'ios' ? 20 : 20,
    right: 16,
    zIndex: 1000,
    alignItems: 'flex-end',
  },
  toastContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    minWidth: 150,
    maxWidth: 300,
  },
  toastText: {
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },
});

export default LandingScreen;
